import React, { useState, useEffect } from 'react';
import { FaPlus, FaRegTrashAlt, FaPencilAlt, FaEye } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { Input, message, Popconfirm } from 'antd';
import { normalizeString, getSuccessMessageByModule } from '~/Utils';

import api from '~/services/api';
import errorHandler from '~/Utils/errorHandler';

import PageTitle from '~/components/PageTitle';
import Button from '~/components/Button';
import { TableHeader, Table, TableActions } from '~/components/Table';
import Box from '~/components/Box';
import Can from '~/components/Can';
import Cannot from '~/components/Cannot';

import BankAccountForm from './form';
import { Global } from './styles';
import DefaultLayout from '../_layouts/full';

export default function BankAccount() {
  const [loading, setLoading] = useState(false);
  const [recordData, setRecordData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [searchTerm, setSearchTerm] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const { t } = useTranslation();

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/bankAccounts');
      setRecordData(data);
    } catch (error) {
      errorHandler(error);
    }
    setLoading(false);
  };

  const handleNew = () => {
    setShowForm(true);
  };

  const handleEdit = (id) => {
    setSelectedRecord(id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      await api.delete(`/bankAccounts/${id}`);
      message.success(
        getSuccessMessageByModule(t('screens:bankAccounts.titleSingular'), t('messages:successDeletedFem'))
      );
      await fetchRecords();
    } catch (error) {
      errorHandler(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!showForm) {
      setSelectedRecord(null);
      fetchRecords();
    }
  }, [showForm]);

  useEffect(() => {
    fetchRecords();
  }, []);

  useEffect(() => {
    if (searchTerm && searchTerm.trim() !== '') {
      const results = recordData.filter(
        (item) =>
          item.description.toLowerCase().indexOf(searchTerm.toString().toLowerCase()) !== -1 ||
          normalizeString(item.description).toLowerCase().indexOf(searchTerm.toString().toLowerCase()) !== -1 ||
          item.account_number.toLowerCase().indexOf(searchTerm.toString().toLowerCase()) !== -1 ||
          item.agency.toLowerCase().indexOf(searchTerm.toString().toLowerCase()) !== -1 ||
          item.bank.toLowerCase().indexOf(searchTerm.toString().toLowerCase()) !== -1
      );
      setTableData(results);
    } else {
      setTableData(recordData);
    }
  }, [recordData, searchTerm]);

  const tableColumns = [
    {
      title: t('screens:bankAccounts.data.description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('screens:bankAccounts.data.bank'),
      dataIndex: 'bank',
      key: 'bank',
    },
    {
      title: t('screens:bankAccounts.data.agency'),
      dataIndex: 'agency',
      key: 'agency',
    },
    {
      title: t('screens:bankAccounts.data.account_number'),
      dataIndex: 'account_number',
      key: 'account_number',
    },
    {
      title: t('screens:bankAccounts.data.actions'),
      dataIndex: 'actions',
      key: 'actions',
      width: '140px',
      align: 'center',
      render: (text, record) => (
        <TableActions>
          <Can permission="@bankAccount/edit">
            <Button size="small" title="Editar" onClick={() => handleEdit(record.id)}>
              <FaPencilAlt />
            </Button>
          </Can>
          <Cannot permission="@bankAccount/edit">
            <Can permission="@bankAccount/view">
              <Button style={{ margin: 'auto' }} size="small" title="Visualizar" onClick={() => handleEdit(record.id)}>
                <FaEye />
              </Button>
            </Can>
          </Cannot>
          <Can permission="@bankAccount/delete">
            <Popconfirm
              title={t('messages:confirmDelete')}
              okText={t('messages:yes')}
              onConfirm={() => handleDelete(record.id)}
              cancelText={t('messages:no')}
            >
              <Button size="small" title="Excluir">
                <FaRegTrashAlt />
              </Button>
            </Popconfirm>
          </Can>
        </TableActions>
      ),
    },
  ];

  return (
    <DefaultLayout>
      <PageTitle title={t('screens:bankAccounts.title')}>
        <Can permission="@bankAccount/edit">
          <Button loading={loading} color="primary" onClick={handleNew}>
            <FaPlus />
            {t('screens:bankAccounts.btnNew')}
          </Button>
        </Can>
      </PageTitle>
      <Box>
        <TableHeader>
          <Input.Search onSearch={(value) => setSearchTerm(value)} className="search-field" />
        </TableHeader>
        <Table rowKey="id" loading={loading} dataSource={tableData} columns={tableColumns} />

        <Global />

        <BankAccountForm visible={showForm} bankAccountID={selectedRecord} onClose={() => setShowForm(false)} />
      </Box>
    </DefaultLayout>
  );
}
